import { useState } from "react";
import { X, Loader2, ShieldCheck, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export const StaffLoginModal = ({ open, onClose }) => {
  const { staffLogin, staffVerifyOtp } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("creds"); // creds | otp
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [userId, setUserId] = useState("");
  const [sentInfo, setSentInfo] = useState({});
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const reset = () => { setStep("creds"); setPassword(""); setCode(""); setUserId(""); setSentInfo({}); };
  const close = () => { reset(); setUsername(""); onClose(); };

  const submitCreds = async (e) => {
    e?.preventDefault();
    if (!username || !password) { toast.error("Enter your username and password"); return; }
    setBusy(true);
    try {
      const data = await staffLogin(username, password);
      setUserId(data.userId);
      setSentInfo(data);
      setStep("otp");
      const dest = [data.maskedPhone && `SMS ${data.maskedPhone}`, data.maskedEmail && `email ${data.maskedEmail}`].filter(Boolean).join(" and ");
      toast.success(dest ? `Verification code sent to ${dest}` : "Verification code sent");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (code.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const u = await staffVerifyOtp(userId, code);
      toast.success(`Welcome, ${u.displayName || u.username}!`);
      close();
      const onlyBroadcast = u.role === "staff" && Object.entries(u.permissions || {}).every(([k, v]) => k === "broadcast" || !(v || []).length);
      nav(onlyBroadcast ? "/broadcast" : "/admin");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="staff-login-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-sm rounded-2xl bg-[#121620] border border-[#273042] p-6">
        <button onClick={close} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>

        {step === "creds" ? (
          <>
            <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-6 h-6 text-[#E11D2A]" /><h3 className="text-xl font-extrabold text-white">Staff login</h3></div>
            <p className="text-sm text-slate-400 mb-5">Sign in with your staff username and password. We'll send a verification code to your mobile and email.</p>
            <form onSubmit={submitCreds} className="space-y-3">
              <input value={username} onChange={(e) => setUsername(e.target.value)} data-testid="staff-username-input"
                autoFocus autoCapitalize="none" placeholder="Username or email"
                className="w-full h-12 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#E11D2A]/60" />
              <input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="staff-password-input"
                type="password" placeholder="Password"
                className="w-full h-12 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#E11D2A]/60" />
              <button type="submit" disabled={busy} data-testid="staff-login-submit"
                className="w-full h-12 rounded-xl bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continue"}
              </button>
            </form>
          </>
        ) : (
          <>
            <button onClick={reset} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm mb-3"><ArrowLeft className="w-4 h-4" /> Back</button>
            <div className="flex items-center gap-2 mb-1"><KeyRound className="w-6 h-6 text-[#E11D2A]" /><h3 className="text-xl font-extrabold text-white">Enter verification code</h3></div>
            <p className="text-sm text-slate-400 mb-4" data-testid="staff-otp-sent">
              {[sentInfo.maskedPhone && `SMS ${sentInfo.maskedPhone}`, sentInfo.maskedEmail && `email ${sentInfo.maskedEmail}`].filter(Boolean).join(" and ") ?
                `We sent a 6-digit code to ${[sentInfo.maskedPhone && `SMS ${sentInfo.maskedPhone}`, sentInfo.maskedEmail && `email ${sentInfo.maskedEmail}`].filter(Boolean).join(" and ")}.` :
                "Enter the 6-digit code we sent you."}
            </p>
            <form onSubmit={submitOtp} className="space-y-3">
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))} data-testid="staff-otp-input"
                autoFocus inputMode="numeric" placeholder="••••••"
                className="w-full h-14 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-center text-2xl tracking-[0.5em] font-mono text-white outline-none focus:border-[#E11D2A]/60" />
              <button type="submit" disabled={busy} data-testid="staff-otp-submit"
                className="w-full h-12 rounded-xl bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify & open dashboard"}
              </button>
            </form>
            <button onClick={submitCreds} disabled={busy} className="w-full mt-3 text-sm text-[#E11D2A] hover:underline font-semibold" data-testid="staff-otp-resend">Resend code</button>
          </>
        )}
      </div>
    </div>
  );
};
